import { useRef, useMemo, useEffect, useContext, useCallback } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';
import { smoothScrollToAnchor } from 'libs/node';

import { UserCountryContext } from 'context/UserCountryContext';
import { ContentContext } from 'context/ContentContext';

import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './ComparisonTable.module.scss';

import { data } from './data';

const baseClass = 'comparison-table';

type Props = {
  title?: string;
  description?: string;
};

export const ComparisonTable = ({ title, description }: Props) => {
  const { internationalization } = useContext(ContentContext);
  const { countryCode, loading: countryLoading, fetchCountryCode } = useContext(UserCountryContext);

  const ref = useRef<HTMLDivElement>(null);
  const intersected = useIntersection(ref);

  const currency = useMemo(() => {
    if (!countryCode) {
      return '£';
    }

    return ['gb', 'uk'].includes(countryCode) ? '£' : '€';
  }, [countryCode]);

  useEffect(() => {
    if (intersected) {
      fetchCountryCode();
    }
  }, [intersected, fetchCountryCode]);

  const onSmoothScrollTo = useCallback((ev: React.MouseEvent, anchor: string) => {
    ev.preventDefault();
    smoothScrollToAnchor(anchor);
  }, []);

  return (
    <section ref={ref} className={styles[`${baseClass}-wrapper`]}>
      <div className={styles[`${baseClass}-container`]}>
        {(title || description) && <TitleBlock title={title} description={description} />}

        <div className={styles[`${baseClass}-cards`]}>
          {data.map((card, idx) => (
            <div
              key={idx}
              className={cn(
                styles[`${baseClass}-card`],
                styles[`${baseClass}-card-${card.theme || 'light'}`],
                card.highlight && styles[`${baseClass}-card-highlight`],
              )}
            >
              <div className={styles[`${baseClass}-card-header`]}>
                <div className={styles[`${baseClass}-card-title`]}>
                  <h3>{card.title || internationalization['target_short']}</h3>
                  {card.subtitle && <span className={styles[`${baseClass}-subtitle`]}>{card.subtitle}</span>}
                </div>

                <div
                  className={cn(styles[`${baseClass}-price`], countryLoading && styles[`${baseClass}-price--loading`])}
                >
                  {currency}
                  {card.price}
                  {card.period && <span>{card.period}</span>}
                </div>
              </div>

              <ul className={styles[`${baseClass}-features`]}>
                {card.features.map((f, i) => (
                  <li key={i} className={cn(styles[`${baseClass}-feature`])}>
                    <Icon type={card.theme === 'light' ? 'check' : 'close'} />
                    {f.text}
                  </li>
                ))}
              </ul>

              <div className={styles[`${baseClass}-cta`]}>
                {!card.cta.anchor ? (
                  <Link route={card.cta.link} disabled={!card.cta.link}>
                    <Button
                      type={card.theme === 'light' ? 'blue' : 'transparent'}
                      {...(!card.cta.link && { loadingDelay: 0, cursor: 'default' })}
                      size="xl"
                    >
                      {card.cta.text}
                    </Button>
                  </Link>
                ) : (
                  <a href={`#${card.cta.anchor}`} onClick={(ev) => onSmoothScrollTo(ev, card.cta.anchor!)}>
                    <Button type={card.theme === 'light' ? 'blue' : 'transparent'} size="xl">
                      {card.cta.text}
                    </Button>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
